import { Microfrontend } from '@cian/mf-react';
import { useBooleanState } from '@cian/react-utils';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { NewbuildingBrokerWidgetContext } from './NewbuildingBrokerWidgetContext';
import { useNewbuildingBrokerWidget } from './hooks';
import { TOpenWidgetResultHandler } from './types';
import { NewbuildingBrokerFallbackModal } from '../../components/NewbuildingBrokerFallbackModal';
import { getPartnerRewardsLanding } from '../../selectors/newbuildingBroker';
import {
  IBrokerAnalyticsUser,
  trackFallbackModalOpen,
  trackFallbackModalRetryClick,
} from '../../tracking/newbuilding/broker';
import { IOpenBrokerModalParams } from '../../types/microfrontends/newbuildingBrokerWidget';

interface INewbuildingBrokerWidgetProviderProps {
  children: React.ReactNode;
}

/**
 * Компонент, обеспечивающий загрузку микрофронта брокерских модалок на клиенте.
 *
 * 1. Пользователь кликает на кнопку, и вызывается `handleWidgetOpeningStart`.
 * 2. Если пользователь подходящий, то регистрируем манифест, загружаем ассеты
 * 3. Сеттим в React-стейт манифест. Ждем сеттинга с помощью useEffect.
 * 4. По стейту отрисовывается <Microfrontend />
 * 5. Инициализуруется реф на <Microfrontend />, содержащий локальное API.
 * 6. Вызываем на рефе локальное API, и открывается модалка брокерки.
 */
export const NewbuildingBrokerWidgetProvider: React.FC<INewbuildingBrokerWidgetProviderProps> = ({ children }) => {
  const noRegistrationURL = useSelector(getPartnerRewardsLanding);

  const paramsRef = React.useRef<IOpenBrokerModalParams>();
  const { state: isFallbackModalVisible, setTrue: showFallbackModal, setFalse: closeFallbackModal } = useBooleanState();
  const [isLoading, setLoading] = React.useState(false);

  const handleWidgetOpeningFinish = React.useCallback<TOpenWidgetResultHandler>(
    ({ isError }) => {
      if (isError) {
        const user = paramsRef.current?.trackingParams?.user as IBrokerAnalyticsUser;

        if (!isFallbackModalVisible) {
          trackFallbackModalOpen(user);
          showFallbackModal();
        }
      } else if (isFallbackModalVisible && !isError) {
        closeFallbackModal();
      }
    },
    [closeFallbackModal, isFallbackModalVisible, showFallbackModal],
  );

  const { openWidget, widgetManifest, widgetRef } = useNewbuildingBrokerWidget(handleWidgetOpeningFinish);

  const handleWidgetOpeningStart = React.useCallback(
    async (params: IOpenBrokerModalParams) => {
      if (noRegistrationURL) {
        window.open(noRegistrationURL, '_blank', 'noopener');

        return;
      }

      // Сохраняем в реф для работы с теми же параметрами на случай, если произойдет ошибка.
      paramsRef.current = params;

      await openWidget(params);
    },
    [noRegistrationURL, openWidget],
  );

  const handleRetryClick = React.useCallback(async () => {
    const user = paramsRef.current?.trackingParams?.user as IBrokerAnalyticsUser;
    trackFallbackModalRetryClick(user);

    setLoading(true);
    await openWidget(paramsRef.current as IOpenBrokerModalParams);
    setLoading(false);
  }, [openWidget]);

  return (
    <NewbuildingBrokerWidgetContext.Provider value={handleWidgetOpeningStart}>
      {children}
      {widgetManifest && (
        <Microfrontend
          ref={widgetRef}
          name={widgetManifest.microfrontendName}
          image={widgetManifest.imageVersion}
          runtime={widgetManifest.runtimeName}
          instance={widgetManifest.instanceId}
        />
      )}
      {isFallbackModalVisible && (
        <NewbuildingBrokerFallbackModal
          isLoading={isLoading}
          isOpen={isFallbackModalVisible}
          onClose={closeFallbackModal}
          onRetry={handleRetryClick}
        />
      )}
    </NewbuildingBrokerWidgetContext.Provider>
  );
};
